import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { apiKeyService } from '../services/apiKeyService';
import { syncService } from '../services/syncService';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in on mount
        checkAuth();

        // FIX (FRONTEND-ISSUE-002): Periodic token validation
        // Check token validity every 5 minutes
        const interval = setInterval(checkAuth, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    // FIX (FRONTEND-BUG-001): Cross-client sync for logout propagation
    useEffect(() => {
        if (user) {
            // Start sync polling to detect changes from other clients
            syncService.startPolling((newState, oldState, changes) => {
                if (!newState.token_valid || changes.includes('token_invalidated')) {
                    console.log('🚪 Logout detected from another client');
                    handleRemoteLogout();
                }

                if (changes.includes('api_key_added') || changes.includes('api_key_removed') || changes.includes('api_key_updated')) {
                    console.log('🔑 API key changed by another client');
                    // Notify components to refresh API key status
                    window.dispatchEvent(new CustomEvent('api-key:changed'));
                }
            });

            return () => syncService.stopPolling();
        }
    }, [user]);

    // Listen for logout from other browser tabs (instant sync)
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'jwt_token' && e.newValue === null) {
                console.log('🚪 Logout detected from another tab (same browser)');
                setUser(null);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const checkAuth = async () => {
        if (authService.isAuthenticated()) {
            try {
                const userData = await authService.getCurrentUser();
                setUser(userData);
            } catch (error) {
                console.error('Auth check failed:', error);
                localStorage.removeItem('jwt_token');
                setUser(null);
            }
        }
        setLoading(false);
    };

    const handleRemoteLogout = () => {
        // Logout initiated by another client
        localStorage.removeItem('jwt_token');
        setUser(null);
        // Page will redirect via ProtectedRoute or 401 interceptor
    };

    const login = async (email, password) => {
        try {
            console.log('🔑 Attempting login for:', email);
            const data = await authService.login(email, password);
            console.log('✅ Login API successful, token received');

            console.log('👤 Fetching user data...');
            const userData = await authService.getCurrentUser();
            console.log('✅ User data fetched:', userData);

            setUser(userData);

            // FIX (FRONTEND-ISSUE-003): Fetch API key status on login for multi-device sync
            try {
                const keyStatus = await apiKeyService.getAPIKeyStatus();
                console.log('API key configured:', keyStatus.configured);
            } catch (error) {
                console.error('Failed to fetch API key status:', error);
            }

            return data;
        } catch (error) {
            console.error('❌ Login failed at step:', error);
            console.error('Error details:', error.response?.data || error.message);
            throw error; // Re-throw so LoginPage can catch it
        }
    };

    const register = async (username, email, password) => {
        const data = await authService.register(username, email, password);
        return data;
    };

    const logout = async () => {
        await authService.logout();
        setUser(null);
        // localStorage clear will trigger storage event in other tabs
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
